import React, { useState, useEffect, useMemo } from 'react';
import { 
  Search, Eye, Filter, User, Brain, Sparkles, Briefcase, 
  GraduationCap, Calendar, Clock, RefreshCw, X, ChevronRight,
  ChevronDown, ChevronLeft, Award, CheckCircle2, ArrowUpDown,
  FileText, Layers, TrendingUp, Download, FileDown, ArrowLeft, Users, Lock
} from 'lucide-react';
import * as XLSX from 'xlsx';
import api from '../../api';
import PerformanceReport from '../AiAgents/PerformanceReport';
import { useFeatures } from '../FeatureGate';

const PAGE_SIZE = 15;

// Helper to extract score / marks out of diverse AI report JSON structures
const extractReportScore = (report) => {
  if (!report || !report.report) return 0;
  const r = report.report;
  
  // Direct score attributes across interview and counselling sessions
  const candidates = [
    r.composite_score,
    r.overall_score,
    r.topic_mastery,
    r.career_clarity,
    r.score,
    r.final_score,
    r.readiness_score
  ];

  for (const c of candidates) {
    if (c !== undefined && c !== null && !isNaN(Number(c))) {
      const num = Number(c);
      if (num >= 0) return Math.min(100, Math.round(num));
    }
  }

  // Fallback: Average of question-by-question breakdown if overall is 0/missing
  if (Array.isArray(r.qa_breakdown) && r.qa_breakdown.length > 0) {
    const scores = r.qa_breakdown.map(q => Number(q.score)).filter(s => !isNaN(s) && s > 0);
    if (scores.length > 0) {
      return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
    }
  }

  return 0;
};

// Styling badge helper for score ranges
const getScoreBadgeProps = (score) => {
  if (score >= 80) {
    return { bg: '#dcfce7', text: '#15803d', border: '#bbf7d0', label: 'Excellent' };
  }
  if (score >= 60) {
    return { bg: '#fef3c7', text: '#b45309', border: '#fde68a', label: 'Satisfactory' };
  }
  if (score > 0) {
    return { bg: '#eff6ff', text: '#1d4ed8', border: '#bfdbfe', label: 'Needs Improvement' };
  }
  return { bg: '#f1f5f9', text: '#64748b', border: '#e2e8f0', label: 'Unscored' };
};

const UniAiReports = ({ showToast }) => {
  const { isSubEnabled, isEnabled } = useFeatures();
  const [serverCanViewReport, setServerCanViewReport] = useState(true);
  const canViewReport = isEnabled('ai_student_reports') && isSubEnabled('ai_student_reports', 'view_report') && serverCanViewReport;

  const [reports, setReports] = useState([]);
  const [serverBatches, setServerBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [batchSearchTerm, setBatchSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [modeFilter, setModeFilter] = useState('all'); // 'all', 'interviewer', 'counsellor'
  const [sortBy, setSortBy] = useState('score_desc'); // 'score_desc', 'score_asc', 'last_report_desc', 'last_report_asc', 'name_asc', 'name_desc', 'reports_desc'
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedStudentKeys, setExpandedStudentKeys] = useState(new Set());
  
  // Modal state for viewing a specific report
  const [selectedReport, setSelectedReport] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async (isManualRefresh = false) => {
    if (isManualRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      const res = await api.get('/university/ai-reports');
      setReports(res.data.reports || []);
      setServerBatches(res.data.batches || []);
      if (res.data.can_view_report !== undefined) {
        setServerCanViewReport(Boolean(res.data.can_view_report));
      }
      if (isManualRefresh && showToast) {
        showToast('AI student reports updated successfully', 'success');
      }
    } catch (err) {
      console.error("Error fetching AI reports:", err);
      if (showToast) showToast('Failed to load AI student reports', 'error');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Aggregate batches data across all university batches & reports
  const batchesData = useMemo(() => {
    const map = {};

    // 1. Initialize from all batches configured in university
    serverBatches.forEach(b => {
      const label = String(b).trim();
      if (label && !map[label]) {
        map[label] = {
          label,
          reports: [],
          uniqueStudentsMap: new Set(),
          interviewCount: 0,
          counsellorCount: 0,
          highestScore: 0,
          totalScore: 0
        };
      }
    });

    // 2. Aggregate from loaded reports
    reports.forEach(rawReport => {
      const repScore = extractReportScore(rawReport);
      const repWithScore = { ...rawReport, score: repScore };
      const batchLabel = (rawReport.academicBatch || rawReport.graduationYear || 'Unknown Batch').trim();
      if (!map[batchLabel]) {
        map[batchLabel] = {
          label: batchLabel,
          reports: [],
          uniqueStudentsMap: new Set(),
          interviewCount: 0,
          counsellorCount: 0,
          highestScore: 0,
          totalScore: 0
        };
      }
      map[batchLabel].reports.push(repWithScore);
      const studentIdKey = rawReport.usn && String(rawReport.usn).trim() !== '' && String(rawReport.usn).trim() !== 'N/A'
        ? `usn_${String(rawReport.usn).trim()}`
        : `name_${String(rawReport.studentName || '').toLowerCase()}`;
      map[batchLabel].uniqueStudentsMap.add(studentIdKey);
      if (rawReport.mode === 'interviewer') map[batchLabel].interviewCount++;
      if (rawReport.mode === 'counsellor') map[batchLabel].counsellorCount++;
      if (repScore > map[batchLabel].highestScore) map[batchLabel].highestScore = repScore;
      map[batchLabel].totalScore += repScore;
    });

    return Object.values(map)
      .map(b => ({
        label: b.label,
        totalReports: b.reports.length,
        uniqueStudents: b.uniqueStudentsMap.size,
        interviewCount: b.interviewCount,
        counsellorCount: b.counsellorCount,
        highestScore: b.highestScore,
        averageScore: b.reports.length > 0 ? Math.round(b.totalScore / b.reports.length) : 0
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [serverBatches, reports]);

  // Overall institutional statistics across all batches
  const overallMetrics = useMemo(() => {
    const totalReports = reports.length;
    const totalBatches = batchesData.length;
    const uniqueStudentsSet = new Set(
      reports.map(r => r.usn && String(r.usn).trim() !== '' && String(r.usn).trim() !== 'N/A' 
        ? `usn_${String(r.usn).trim()}` 
        : `name_${String(r.studentName || '').toLowerCase()}`
      )
    );
    const totalScores = reports.reduce((acc, r) => acc + extractReportScore(r), 0);
    const averageScore = totalReports > 0 ? Math.round(totalScores / totalReports) : 0;
    return {
      totalReports,
      totalBatches,
      uniqueStudents: uniqueStudentsSet.size,
      averageScore
    };
  }, [reports, batchesData]);

  // Active reports scoped to selected batch
  const activeReports = useMemo(() => {
    if (!selectedBatch) return reports;
    return reports.filter(r => (r.academicBatch || r.graduationYear || 'Unknown Batch').trim() === selectedBatch.trim());
  }, [reports, selectedBatch]);

  // Group active reports by Student with computed scores
  const studentsData = useMemo(() => {
    const studentMap = {};

    activeReports.forEach(rawReport => {
      // Calculate score for each individual report
      const repScore = extractReportScore(rawReport);
      const report = { ...rawReport, score: repScore };

      const usnClean = report.usn && String(report.usn).trim() !== '' && String(report.usn).trim() !== 'N/A' 
        ? String(report.usn).trim() 
        : null;
      
      const nameClean = String(report.studentName || 'Unknown Student').trim();
      const key = usnClean ? `usn_${usnClean}` : `name_${nameClean.toLowerCase()}`;

      if (!studentMap[key]) {
        studentMap[key] = {
          key,
          studentName: nameClean,
          usn: usnClean || 'N/A',
          academicBatch: report.academicBatch || selectedBatch || 'Unknown Batch',
          reports: [],
          interviewCount: 0,
          counsellorCount: 0,
          highestScore: 0,
          averageScore: 0,
          lastReportDate: null,
          lastReport: null
        };
      }

      studentMap[key].reports.push(report);
      if (report.mode === 'interviewer') studentMap[key].interviewCount++;
      if (report.mode === 'counsellor') studentMap[key].counsellorCount++;
    });

    // Sort each student's reports newest first, compute highest score and last report
    Object.values(studentMap).forEach(s => {
      s.reports.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
      s.lastReport = s.reports[0] || null;
      s.lastReportDate = s.lastReport && s.lastReport.createdAt ? new Date(s.lastReport.createdAt) : new Date(0);
      s.highestScore = s.reports.length > 0 ? Math.max(...s.reports.map(r => r.score || 0)) : 0;
      s.averageScore = s.reports.length > 0
        ? Math.round(s.reports.reduce((acc, r) => acc + (r.score || 0), 0) / s.reports.length)
        : 0;
    });

    return Object.values(studentMap);
  }, [activeReports, selectedBatch]);

  // High-level metrics for active batch
  const metrics = useMemo(() => {
    const totalReports = activeReports.length;
    const interviewCount = activeReports.filter(r => r.mode === 'interviewer').length;
    const counsellorCount = activeReports.filter(r => r.mode === 'counsellor').length;
    const uniqueStudents = studentsData.length;

    return { totalReports, interviewCount, counsellorCount, uniqueStudents };
  }, [activeReports, studentsData]);

  // Filtering and sorting the student list
  const filteredStudents = useMemo(() => {
    let list = studentsData.filter(student => {
      const q = searchTerm.toLowerCase().trim();
      const matchesSearch = !q || 
        (student.studentName && student.studentName.toLowerCase().includes(q)) ||
        (student.usn && String(student.usn).toLowerCase().includes(q));
      
      const matchesMode = modeFilter === 'all' || 
        (modeFilter === 'interviewer' && student.interviewCount > 0) ||
        (modeFilter === 'counsellor' && student.counsellorCount > 0);

      return matchesSearch && matchesMode;
    });

    return list.sort((a, b) => {
      if (sortBy === 'score_desc') {
        if (b.highestScore !== a.highestScore) {
          return b.highestScore - a.highestScore;
        }
        return b.lastReportDate - a.lastReportDate;
      }
      if (sortBy === 'score_asc') {
        if (a.highestScore !== b.highestScore) {
          return a.highestScore - b.highestScore;
        }
        return b.lastReportDate - a.lastReportDate;
      }
      if (sortBy === 'last_report_desc') {
        return b.lastReportDate - a.lastReportDate;
      }
      if (sortBy === 'last_report_asc') {
        return a.lastReportDate - b.lastReportDate;
      }
      if (sortBy === 'name_asc') {
        return (a.studentName || '').localeCompare(b.studentName || '');
      }
      if (sortBy === 'name_desc') {
        return (b.studentName || '').localeCompare(a.studentName || '');
      }
      if (sortBy === 'reports_desc') {
        return b.reports.length - a.reports.length;
      }
      return 0;
    });
  }, [studentsData, searchTerm, modeFilter, sortBy]);

  // Reset page when filter/search/sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, modeFilter, sortBy]);

  // Pagination calculations (15 students per page)
  const totalPages = Math.ceil(filteredStudents.length / PAGE_SIZE) || 1;
  const paginatedStudents = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredStudents.slice(start, start + PAGE_SIZE);
  }, [filteredStudents, currentPage]);

  // Toggle student expansion in accordion
  const toggleStudentExpanded = (studentKey) => {
    if (!canViewReport) return;
    setExpandedStudentKeys(prev => {
      const next = new Set(prev);
      if (next.has(studentKey)) {
        next.delete(studentKey);
      } else {
        next.add(studentKey);
      }
      return next;
    });
  };

  const expandAll = () => {
    if (!canViewReport) return;
    const allKeys = new Set(paginatedStudents.map(s => s.key));
    setExpandedStudentKeys(allKeys);
  };

  const collapseAll = () => {
    setExpandedStudentKeys(new Set());
  };

  const handleViewReport = (reportItem) => {
    if (!canViewReport) {
      if (showToast) showToast('Viewing detailed evaluation reports is disabled by administrator.', 'warning');
      return;
    }
    setSelectedReport(reportItem);
    setShowModal(true);
  };

  const handleExportExcel = () => {
    if (!filteredStudents || filteredStudents.length === 0) {
      if (showToast) showToast('No student records available to export', 'error');
      return;
    }

    try {
      const exportRows = [];
      filteredStudents.forEach(s => {
        if (s.reports.length === 0) {
          exportRows.push({
            'Student Name': s.studentName,
            'USN / Roll Number': s.usn,
            'Batch': selectedBatch || s.academicBatch || 'N/A',
            'Total Completed Sessions': 0,
            'Highest Mark': s.highestScore,
            'Average Mark': s.averageScore,
            'Session Mode': 'None',
            'Session Date': 'N/A',
            'Session Role/Focus': 'N/A',
            'Session Score': 'N/A'
          });
        } else {
          s.reports.forEach((rep, idx) => {
            exportRows.push({
              'Student Name': s.studentName,
              'USN / Roll Number': s.usn,
              'Batch': selectedBatch || rep.academicBatch || s.academicBatch || 'N/A',
              'Total Completed Sessions': s.reports.length,
              'Highest Mark': s.highestScore,
              'Average Mark': s.averageScore,
              'Session #': idx + 1,
              'Session Mode': rep.mode === 'interviewer' ? 'AI Interviewer' : 'AI Counsellor',
              'Session Date': rep.createdAt ? new Date(rep.createdAt).toLocaleString('en-IN') : 'N/A',
              'Session Role/Focus': rep.report?.job_role || rep.report?.role || (rep.mode === 'interviewer' ? 'Mock Interview' : 'Career Counselling'),
              'Session Score': rep.score || 0
            });
          });
        }
      });

      const worksheet = XLSX.utils.json_to_sheet(exportRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'AI Student Reports');
      const filename = selectedBatch
        ? `University_AI_Student_Reports_Batch_${String(selectedBatch).replace(/[^a-zA-Z0-9_-]/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`
        : `University_AI_Student_Reports_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, filename);
      if (showToast) showToast('Student reports successfully exported to Excel', 'success');
    } catch (err) {
      console.error('Failed to export reports to Excel:', err);
      if (showToast) showToast('Failed to export reports to Excel', 'error');
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedReport(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return { date: 'N/A', time: '' };
    try {
      const d = new Date(dateString);
      return {
        date: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        time: d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })
      };
    } catch {
      return { date: dateString, time: '' };
    }
  };

  // Student Initials
  const getInitials = (name) => {
    if (!name) return 'S';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  // Color generator for avatar based on name
  const getAvatarColor = (name) => {
    const colors = [
      { bg: '#eff6ff', text: '#2563eb', border: '#bfdbfe' },
      { bg: '#f5f3ff', text: '#7c3aed', border: '#ddd6fe' },
      { bg: '#ecfdf5', text: '#059669', border: '#a7f3d0' },
      { bg: '#fff7ed', text: '#d97706', border: '#fed7aa' },
      { bg: '#fdf2f8', text: '#db2777', border: '#fbcfe8' },
    ];
    let hash = 0;
    for (let i = 0; i < (name || '').length; i++) hash += name.charCodeAt(i);
    return colors[Math.abs(hash) % colors.length];
  };

  // ══════════════════════════════════════════
  //  RENDER: Batches Selection View
  // ══════════════════════════════════════════
  const renderBatchesView = () => {
    const filteredBatches = batchesData.filter(b => 
      !batchSearchTerm || b.label.toLowerCase().includes(batchSearchTerm.toLowerCase().trim())
    );

    return (
      <div className="unidash-fade-in" style={{ padding: '24px 0', maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
              <div style={{ 
                width: '40px', 
                height: '40px', 
                borderRadius: '10px', 
                background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                color: '#ffffff',
                boxShadow: '0 4px 10px rgba(37, 99, 235, 0.25)'
              }}>
                <Brain size={22} />
              </div>
              <h2 style={{ fontSize: '26px', fontWeight: '800', color: '#0f172a', margin: 0, letterSpacing: '-0.02em' }}>
                AI Student Reports
              </h2>
            </div>
            <p style={{ color: '#64748b', margin: 0, fontSize: '15px' }}>
              Select an academic student batch to view evaluation reports and performance scorecards.
            </p>
          </div>

          <button 
            onClick={() => fetchReports(true)}
            disabled={refreshing}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 18px',
              borderRadius: '10px',
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              color: '#334155',
              fontSize: '14px',
              fontWeight: '600',
              cursor: refreshing ? 'not-allowed' : 'pointer',
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
              transition: 'all 0.15s ease'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.background = '#f8fafc'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.background = '#ffffff'; }}
          >
            <RefreshCw size={16} style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
            <span>{refreshing ? 'Refreshing...' : 'Refresh Reports'}</span>
          </button>
        </div>

        {/* Institutional Summary KPIs */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px', marginBottom: '28px' }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '14px',
            border: '1px solid #e2e8f0',
            borderLeft: '5px solid #2563eb',
            padding: '20px 24px',
            boxShadow: '0 2px 6px rgba(0,0,0,0.02)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <div style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>
                Student Batches
              </div>
              <div style={{ fontSize: '32px', fontWeight: '800', color: '#0f172a', lineHeight: 1.1 }}>
                {overallMetrics.totalBatches}
              </div>
              <div style={{ fontSize: '12px', color: '#2563eb', fontWeight: '600', marginTop: '6px' }}>
                Academic Cohorts
              </div>
            </div>
            <div style={{ width: '52px', height: '52px', borderRadius: '12px', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#2563eb' }}>
              <Layers size={26} />
            </div>
          </div>

          <div style={{
            background: '#ffffff',
            borderRadius: '14px',
            border: '1px solid #e2e8f0',
            borderLeft: '5px solid #7c3aed',
            padding: '20px 24px',
            boxShadow: '0 2px 6px rgba(0,0,0,0.02)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <div style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>
                Assessed Students
              </div>
              <div style={{ fontSize: '32px', fontWeight: '800', color: '#0f172a', lineHeight: 1.1 }}>
                {overallMetrics.uniqueStudents}
              </div>
              <div style={{ fontSize: '12px', color: '#7c3aed', fontWeight: '600', marginTop: '6px' }}>
                With AI Reports
              </div>
            </div>
            <div style={{ width: '52px', height: '52px', borderRadius: '12px', background: '#f5f3ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#7c3aed' }}>
              <GraduationCap size={26} />
            </div>
          </div>

          <div style={{
            background: '#ffffff',
            borderRadius: '14px',
            border: '1px solid #e2e8f0',
            borderLeft: '5px solid #059669',
            padding: '20px 24px',
            boxShadow: '0 2px 6px rgba(0,0,0,0.02)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <div style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>
                Total Evaluations
              </div>
              <div style={{ fontSize: '32px', fontWeight: '800', color: '#0f172a', lineHeight: 1.1 }}>
                {overallMetrics.totalReports}
              </div>
              <div style={{ fontSize: '12px', color: '#059669', fontWeight: '600', marginTop: '6px' }}>
                Generated Sessions
              </div>
            </div>
            <div style={{ width: '52px', height: '52px', borderRadius: '12px', background: '#ecfdf5', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#059669' }}>
              <Award size={26} />
            </div>
          </div>

          <div style={{
            background: '#ffffff',
            borderRadius: '14px',
            border: '1px solid #e2e8f0',
            borderLeft: '5px solid #d97706',
            padding: '20px 24px',
            boxShadow: '0 2px 6px rgba(0,0,0,0.02)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <div style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>
                Average Marks
              </div>
              <div style={{ fontSize: '32px', fontWeight: '800', color: '#0f172a', lineHeight: 1.1 }}>
                {overallMetrics.averageScore > 0 ? `${overallMetrics.averageScore}%` : 'N/A'}
              </div>
              <div style={{ fontSize: '12px', color: '#d97706', fontWeight: '600', marginTop: '6px' }}>
                Institutional Average
              </div>
            </div>
            <div style={{ width: '52px', height: '52px', borderRadius: '12px', background: '#fffbeb', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#d97706' }}>
              <TrendingUp size={26} />
            </div>
          </div>
        </div>

        {/* Batches Search bar */}
        {batchesData.length > 2 && (
          <div style={{ marginBottom: '20px', maxWidth: '380px', position: 'relative' }}>
            <Search size={16} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
            <input
              type="text"
              placeholder="Search batches..."
              value={batchSearchTerm}
              onChange={(e) => setBatchSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 14px 10px 38px',
                borderRadius: '10px',
                border: '1px solid #cbd5e1',
                fontSize: '14px',
                outline: 'none',
                background: '#ffffff',
                boxSizing: 'border-box'
              }}
            />
          </div>
        )}

        {/* Batches Grid */}
        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 20px', color: '#64748b' }}>
            <RefreshCw size={32} style={{ animation: 'spin 1s linear infinite', color: '#2563eb', marginBottom: '12px' }} />
            <p style={{ margin: 0, fontSize: '15px', fontWeight: '600' }}>Loading batches and AI evaluation reports...</p>
          </div>
        ) : filteredBatches.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', background: '#ffffff', borderRadius: '16px', border: '1px solid #e2e8f0' }}>
            <Layers size={44} style={{ color: '#94a3b8', opacity: 0.5, marginBottom: '12px' }} />
            <h3 style={{ margin: '0 0 6px', fontSize: '18px', fontWeight: '700', color: '#0f172a' }}>
              No student batches found
            </h3>
            <p style={{ margin: 0, fontSize: '14px', color: '#64748b' }}>
              {batchSearchTerm ? 'No batches match your search term.' : 'Your institution does not have any student batches configured yet.'}
            </p>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            gap: '20px'
          }}>
            {filteredBatches.map(b => {
              return (
                <div
                  key={b.label}
                  onClick={() => {
                    setSelectedBatch(b.label);
                    setCurrentPage(1);
                    setSearchTerm('');
                    setModeFilter('all');
                  }}
                  style={{
                    background: '#ffffff',
                    borderRadius: '16px',
                    border: '1px solid #e2e8f0',
                    padding: '22px',
                    cursor: 'pointer',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#3b82f6';
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    e.currentTarget.style.boxShadow = '0 10px 24px rgba(59, 130, 246, 0.12)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#e2e8f0';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.03)';
                  }}
                >
                  <div>
                    {/* Top row */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{
                          width: '42px',
                          height: '42px',
                          borderRadius: '11px',
                          background: 'rgba(59, 130, 246, 0.1)',
                          color: '#2563eb',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <Users size={20} />
                        </div>
                        <div>
                          <div style={{ fontSize: '17px', fontWeight: '800', color: '#0f172a' }}>
                            Batch {b.label}
                          </div>
                          <div style={{ fontSize: '12px', color: '#64748b' }}>
                            {b.uniqueStudents} candidate{b.uniqueStudents !== 1 ? 's' : ''} assessed
                          </div>
                        </div>
                      </div>

                      <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        background: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#64748b'
                      }}>
                        <ChevronRight size={16} />
                      </div>
                    </div>

                    {/* Stats pills */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px' }}>
                      <div style={{
                        background: '#f8fafc',
                        borderRadius: '10px',
                        padding: '10px 12px',
                        border: '1px solid #f1f5f9'
                      }}>
                        <div style={{ fontSize: '11px', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                          Reports
                        </div>
                        <div style={{ fontSize: '18px', fontWeight: '800', color: '#0f172a', marginTop: '2px' }}>
                          {b.totalReports}
                        </div>
                      </div>

                      <div style={{
                        background: b.totalReports > 0 ? '#eff6ff' : '#f8fafc',
                        borderRadius: '10px',
                        padding: '10px 12px',
                        border: b.totalReports > 0 ? '1px solid #bfdbfe' : '1px solid #f1f5f9'
                      }}>
                        <div style={{ fontSize: '11px', color: b.totalReports > 0 ? '#1d4ed8' : '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                          Avg Marks
                        </div>
                        <div style={{ fontSize: '18px', fontWeight: '800', color: b.totalReports > 0 ? '#1d4ed8' : '#64748b', marginTop: '2px' }}>
                          {b.totalReports > 0 ? `${b.averageScore}%` : 'N/A'}
                        </div>
                      </div>
                    </div>

                    {/* Breakdown */}
                    <div style={{ fontSize: '12px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '14px' }}>
                      <Award size={13} style={{ color: '#8b5cf6' }} />
                      <span>Highest Mark: <strong>{b.highestScore > 0 ? `${b.highestScore}%` : 'N/A'}</strong></span>
                      <span style={{ margin: '0 4px', color: '#cbd5e1' }}>•</span>
                      <span>{b.interviewCount} Interview{b.interviewCount !== 1 ? 's' : ''}</span>
                    </div>
                  </div>

                  {/* Card Action footer */}
                  <div style={{
                    paddingTop: '12px',
                    borderTop: '1px solid #f1f5f9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    color: canViewReport ? '#2563eb' : '#64748b',
                    fontSize: '13px',
                    fontWeight: '700'
                  }}>
                    <span>{canViewReport ? 'View Student Reports' : 'View Cohort Students'}</span>
                    <ChevronRight size={15} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // If no batch is selected, show Batches selection view
  if (!selectedBatch) {
    return renderBatchesView();
  }

  return (
    <div className="unidash-fade-in" style={{ padding: '24px 0', maxWidth: '1400px', margin: '0 auto' }}>
      
      {/* ── HEADER (BATCH DETAIL VIEW) ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <button
            onClick={() => {
              setSelectedBatch(null);
              setSearchTerm('');
              setModeFilter('all');
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 14px',
              borderRadius: '8px',
              border: '1px solid #cbd5e1',
              background: '#ffffff',
              color: '#334155',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              marginBottom: '14px',
              boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
              transition: 'all 0.15s ease'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#3b82f6'; e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.color = '#1d4ed8'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.background = '#ffffff'; e.currentTarget.style.color = '#334155'; }}
          >
            <ArrowLeft size={15} />
            <span>Back to All Batches</span>
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
            <div style={{ 
              width: '40px', 
              height: '40px', 
              borderRadius: '10px', 
              background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              color: '#ffffff',
              boxShadow: '0 4px 10px rgba(37, 99, 235, 0.25)'
            }}>
              <Brain size={22} />
            </div>
            <h2 style={{ fontSize: '26px', fontWeight: '800', color: '#0f172a', margin: 0, letterSpacing: '-0.02em' }}>
              Reports Dashboard — Batch {selectedBatch}
            </h2>
          </div>
          <p style={{ color: '#64748b', margin: 0, fontSize: '15px' }}>
            Browse student candidates in Batch {selectedBatch}, sort by highest marks, and click on any student's name to explore all evaluation scorecards.
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <button 
            onClick={handleExportExcel}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 18px',
              borderRadius: '10px',
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              color: '#059669',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
              transition: 'all 0.15s ease'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#a7f3d0'; e.currentTarget.style.background = '#ecfdf5'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.background = '#ffffff'; }}
            title="Download full summary report for all students in Excel"
          >
            <Download size={16} />
            <span>Export to Excel</span>
          </button>

          <button 
            onClick={() => fetchReports(true)}
            disabled={refreshing}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 18px',
              borderRadius: '10px',
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              color: '#334155',
              fontSize: '14px',
              fontWeight: '600',
              cursor: refreshing ? 'not-allowed' : 'pointer',
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
              transition: 'all 0.15s ease'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.background = '#f8fafc'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.background = '#ffffff'; }}
          >
            <RefreshCw size={16} className={refreshing ? 'spin-animation' : ''} style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
            <span>{refreshing ? 'Refreshing...' : 'Refresh Reports'}</span>
          </button>
        </div>
      </div>

      {/* ── METRIC STAT CARDS ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px', marginBottom: '28px' }}>
        
        {/* Card 1: Unique Students */}
        <div style={{
          background: '#ffffff',
          borderRadius: '14px',
          border: '1px solid #e2e8f0',
          borderLeft: '5px solid #2563eb',
          padding: '20px 24px',
          boxShadow: '0 2px 6px rgba(0,0,0,0.02)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <div style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>
              Enrolled Students
            </div>
            <div style={{ fontSize: '32px', fontWeight: '800', color: '#0f172a', lineHeight: 1.1 }}>
              {metrics.uniqueStudents}
            </div>
            <div style={{ fontSize: '12px', color: '#2563eb', fontWeight: '600', marginTop: '6px' }}>
              Candidates Assessed
            </div>
          </div>
          <div style={{ width: '52px', height: '52px', borderRadius: '12px', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#2563eb' }}>
            <GraduationCap size={26} />
          </div>
        </div>

        {/* Card 2: Total Reports */}
        <div style={{
          background: '#ffffff',
          borderRadius: '14px',
          border: '1px solid #e2e8f0',
          borderLeft: '5px solid #7c3aed',
          padding: '20px 24px',
          boxShadow: '0 2px 6px rgba(0,0,0,0.02)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <div style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>
              Total Evaluations
            </div>
            <div style={{ fontSize: '32px', fontWeight: '800', color: '#0f172a', lineHeight: 1.1 }}>
              {metrics.totalReports}
            </div>
            <div style={{ fontSize: '12px', color: '#7c3aed', fontWeight: '600', marginTop: '6px' }}>
              All Generated Sessions
            </div>
          </div>
          <div style={{ width: '52px', height: '52px', borderRadius: '12px', background: '#f5f3ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#7c3aed' }}>
            <Award size={26} />
          </div>
        </div>

        {/* Card 3: AI Interviews */}
        <div style={{
          background: '#ffffff',
          borderRadius: '14px',
          border: '1px solid #e2e8f0',
          borderLeft: '5px solid #059669',
          padding: '20px 24px',
          boxShadow: '0 2px 6px rgba(0,0,0,0.02)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <div style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>
              AI Interviews
            </div>
            <div style={{ fontSize: '32px', fontWeight: '800', color: '#0f172a', lineHeight: 1.1 }}>
              {metrics.interviewCount}
            </div>
            <div style={{ fontSize: '12px', color: '#059669', fontWeight: '600', marginTop: '6px' }}>
              Mock Interviews Taken
            </div>
          </div>
          <div style={{ width: '52px', height: '52px', borderRadius: '12px', background: '#ecfdf5', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#059669' }}>
            <Briefcase size={26} />
          </div>
        </div>

        {/* Card 4: AI Counselling */}
        <div style={{
          background: '#ffffff',
          borderRadius: '14px',
          border: '1px solid #e2e8f0',
          borderLeft: '5px solid #d97706',
          padding: '20px 24px',
          boxShadow: '0 2px 6px rgba(0,0,0,0.02)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <div style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>
              AI Counselling
            </div>
            <div style={{ fontSize: '32px', fontWeight: '800', color: '#0f172a', lineHeight: 1.1 }}>
              {metrics.counsellorCount}
            </div>
            <div style={{ fontSize: '12px', color: '#d97706', fontWeight: '600', marginTop: '6px' }}>
              Career & Guidance
            </div>
          </div>
          <div style={{ width: '52px', height: '52px', borderRadius: '12px', background: '#fffbeb', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#d97706' }}>
            <Sparkles size={26} />
          </div>
        </div>

      </div>

      {/* ── FILTER & SORT CONTROLS ── */}
      <div style={{
        background: '#ffffff',
        borderRadius: '14px',
        border: '1px solid #e2e8f0',
        padding: '16px 20px',
        marginBottom: '20px',
        boxShadow: '0 2px 6px rgba(0,0,0,0.02)',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '16px'
      }}>
        
        {/* Search Input */}
        <div style={{ position: 'relative', flex: '1', minWidth: '280px', maxWidth: '380px' }}>
          <Search size={18} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
          <input
            type="text"
            placeholder="Search student name or USN..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 38px 10px 40px',
              borderRadius: '10px',
              border: '1px solid #cbd5e1',
              background: '#f8fafc',
              color: '#0f172a',
              fontSize: '14px',
              outline: 'none',
              transition: 'border-color 0.15s ease'
            }}
            onFocus={(e) => { e.target.style.borderColor = '#2563eb'; e.target.style.background = '#ffffff'; }}
            onBlur={(e) => { e.target.style.borderColor = '#cbd5e1'; e.target.style.background = '#f8fafc'; }}
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'transparent',
                border: 'none',
                color: '#94a3b8',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                padding: '2px'
              }}
            >
              <X size={16} />
            </button>
          )}
        </div>

        {/* Filters, Sort & Expand Actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          
          {/* Agent Mode Segmented Tabs */}
          <div style={{
            display: 'inline-flex',
            background: '#f1f5f9',
            padding: '4px',
            borderRadius: '10px',
            border: '1px solid #e2e8f0',
            gap: '4px'
          }}>
            <button
              onClick={() => setModeFilter('all')}
              style={{
                border: 'none',
                background: modeFilter === 'all' ? '#ffffff' : 'transparent',
                color: modeFilter === 'all' ? '#0f172a' : '#64748b',
                fontWeight: modeFilter === 'all' ? '700' : '500',
                fontSize: '13px',
                padding: '6px 12px',
                borderRadius: '7px',
                cursor: 'pointer',
                boxShadow: modeFilter === 'all' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                transition: 'all 0.15s ease'
              }}
            >
              All Students ({metrics.uniqueStudents})
            </button>

            <button
              onClick={() => setModeFilter('interviewer')}
              style={{
                border: 'none',
                background: modeFilter === 'interviewer' ? '#ffffff' : 'transparent',
                color: modeFilter === 'interviewer' ? '#2563eb' : '#64748b',
                fontWeight: modeFilter === 'interviewer' ? '700' : '500',
                fontSize: '13px',
                padding: '6px 12px',
                borderRadius: '7px',
                cursor: 'pointer',
                boxShadow: modeFilter === 'interviewer' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                transition: 'all 0.15s ease'
              }}
            >
              Interviewers
            </button>

            <button
              onClick={() => setModeFilter('counsellor')}
              style={{
                border: 'none',
                background: modeFilter === 'counsellor' ? '#ffffff' : 'transparent',
                color: modeFilter === 'counsellor' ? '#059669' : '#64748b',
                fontWeight: modeFilter === 'counsellor' ? '700' : '500',
                fontSize: '13px',
                padding: '6px 12px',
                borderRadius: '7px',
                cursor: 'pointer',
                boxShadow: modeFilter === 'counsellor' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                transition: 'all 0.15s ease'
              }}
            >
              Counsellors
            </button>
          </div>

          {/* Sort Dropdown with Highest Marks Option */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ArrowUpDown size={15} color="#64748b" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{
                padding: '8px 12px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                background: '#ffffff',
                color: '#334155',
                fontSize: '13px',
                fontWeight: '600',
                outline: 'none',
                cursor: 'pointer'
              }}
            >
              <option value="score_desc">Highest Marks / Score First</option>
              <option value="score_asc">Lowest Marks / Score First</option>
              <option value="last_report_desc">Last Report (Newest First)</option>
              <option value="last_report_asc">Last Report (Oldest First)</option>
              <option value="name_asc">Student Name (A → Z)</option>
              <option value="name_desc">Student Name (Z → A)</option>
              <option value="reports_desc">Most Reports Completed</option>
            </select>
          </div>

          {/* Expand/Collapse All */}
          {paginatedStudents.length > 0 && canViewReport && (
            <button
              onClick={() => {
                if (expandedStudentKeys.size === paginatedStudents.length) collapseAll();
                else expandAll();
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 12px',
                borderRadius: '8px',
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                color: '#475569',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
              title="Expand or collapse visible students"
            >
              <Layers size={14} />
              <span>{expandedStudentKeys.size === paginatedStudents.length ? 'Collapse All' : 'Expand All'}</span>
            </button>
          )}

        </div>

      </div>

      {/* ── STUDENTS LIST TABLE ── */}
      {loading ? (
        <div style={{
          background: '#ffffff',
          borderRadius: '14px',
          border: '1px solid #e2e8f0',
          padding: '60px 20px',
          textAlign: 'center',
          boxShadow: '0 2px 6px rgba(0,0,0,0.02)'
        }}>
          <div className="spinner" style={{ margin: '0 auto 16px' }} />
          <h4 style={{ fontSize: '16px', fontWeight: '700', color: '#1e293b', margin: '0 0 6px 0' }}>
            Loading Student Records...
          </h4>
          <p style={{ color: '#64748b', margin: 0, fontSize: '14px' }}>
            Retrieving enrolled candidates and computing score metrics.
          </p>
        </div>
      ) : filteredStudents.length === 0 ? (
        <div style={{
          background: '#ffffff',
          borderRadius: '14px',
          border: '1px solid #e2e8f0',
          padding: '60px 24px',
          textAlign: 'center',
          boxShadow: '0 2px 6px rgba(0,0,0,0.02)'
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            background: '#f1f5f9',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#94a3b8',
            margin: '0 auto 18px'
          }}>
            <User size={32} />
          </div>
          <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#0f172a', margin: '0 0 8px 0' }}>
            {searchTerm || modeFilter !== 'all' ? 'No Matching Students Found' : 'No Student Evaluations Yet'}
          </h3>
          <p style={{ color: '#64748b', maxWidth: '440px', margin: '0 auto 20px', fontSize: '14px', lineHeight: 1.5 }}>
            {searchTerm || modeFilter !== 'all' 
              ? `No student profiles matched your filter "${searchTerm || modeFilter}". Try searching by another name or USN.`
              : 'As enrolled students take AI Mock Interviews or AI Counselling sessions, they will be listed here with their complete evaluation history.'}
          </p>
          {(searchTerm || modeFilter !== 'all') && (
            <button
              onClick={() => { setSearchTerm(''); setModeFilter('all'); }}
              style={{
                padding: '8px 18px',
                borderRadius: '8px',
                background: '#eff6ff',
                color: '#2563eb',
                border: '1px solid #bfdbfe',
                fontWeight: '600',
                fontSize: '13px',
                cursor: 'pointer'
              }}
            >
              Clear Filters
            </button>
          )}
        </div>
      ) : (
        <div style={{
          background: '#ffffff',
          borderRadius: '14px',
          border: '1px solid #e2e8f0',
          overflow: 'hidden',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.04), 0 2px 4px -2px rgba(0, 0, 0, 0.04)'
        }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: '920px' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  
                  {/* Student Candidate (Sortable) */}
                  <th 
                    onClick={() => setSortBy(sortBy === 'name_asc' ? 'name_desc' : 'name_asc')}
                    style={{ 
                      padding: '16px 24px', 
                      fontSize: '12px', 
                      fontWeight: '700', 
                      color: '#64748b', 
                      textTransform: 'uppercase', 
                      letterSpacing: '0.05em', 
                      width: '28%',
                      cursor: 'pointer',
                      userSelect: 'none'
                    }}
                    title="Click to sort by student name"
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span>Student Candidate{canViewReport ? ' (Click to view)' : ''}</span>
                      <ArrowUpDown size={13} color="#94a3b8" />
                    </div>
                  </th>

                  {/* USN */}
                  <th style={{ padding: '16px 18px', fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', width: '13%' }}>
                    USN / Roll No
                  </th>

                  {/* Completed Sessions */}
                  <th style={{ padding: '16px 18px', fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', width: '15%' }}>
                    Sessions
                  </th>

                  {/* Highest Marks Column (Sortable) */}
                  <th 
                    onClick={() => setSortBy(sortBy === 'score_desc' ? 'score_asc' : 'score_desc')}
                    style={{ 
                      padding: '16px 18px', 
                      fontSize: '12px', 
                      fontWeight: '700', 
                      color: sortBy.startsWith('score') ? '#2563eb' : '#64748b', 
                      textTransform: 'uppercase', 
                      letterSpacing: '0.05em', 
                      width: '14%',
                      cursor: 'pointer',
                      userSelect: 'none'
                    }}
                    title="Click to sort by highest marks"
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span>Highest Mark</span>
                      <ArrowUpDown size={13} color={sortBy.startsWith('score') ? '#2563eb' : '#94a3b8'} />
                    </div>
                  </th>

                  {/* Last Report Generated (Sortable) */}
                  <th 
                    onClick={() => setSortBy(sortBy === 'last_report_desc' ? 'last_report_asc' : 'last_report_desc')}
                    style={{ 
                      padding: '16px 18px', 
                      fontSize: '12px', 
                      fontWeight: '700', 
                      color: '#64748b', 
                      textTransform: 'uppercase', 
                      letterSpacing: '0.05em', 
                      width: '16%',
                      cursor: 'pointer',
                      userSelect: 'none'
                    }}
                    title="Click to sort by last report date"
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span>Last Generated</span>
                      <ArrowUpDown size={13} color="#94a3b8" />
                    </div>
                  </th>

                  {/* Action */}
                  <th style={{ padding: '16px 24px', fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'right', width: '14%' }}>
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedStudents.map((student) => {
                  const avatarColor = getAvatarColor(student.studentName);
                  const isExpanded = expandedStudentKeys.has(student.key);
                  const lastReportDateInfo = formatDate(student.lastReport?.createdAt);
                  const lastReportMode = student.lastReport?.mode;
                  const scoreBadge = getScoreBadgeProps(student.highestScore);

                  return (
                    <React.Fragment key={student.key}>
                      {/* Main Student Row */}
                      <tr 
                        style={{ 
                          borderBottom: isExpanded ? 'none' : '1px solid #f1f5f9',
                          background: isExpanded ? '#f8fafc' : 'transparent',
                          transition: 'background-color 0.15s ease'
                        }}
                        onMouseEnter={(e) => { if (!isExpanded) e.currentTarget.style.backgroundColor = '#fafafa'; }}
                        onMouseLeave={(e) => { if (!isExpanded) e.currentTarget.style.backgroundColor = 'transparent'; }}
                      >
                        {/* Student Name & Avatar */}
                        <td style={{ padding: '16px 24px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                            <div style={{
                              width: '42px',
                              height: '42px',
                              borderRadius: '50%',
                              background: avatarColor.bg,
                              color: avatarColor.text,
                              border: `1px solid ${avatarColor.border}`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: '700',
                              fontSize: '14px',
                              flexShrink: 0
                            }}>
                              {getInitials(student.studentName)}
                            </div>
                            
                            <div>
                              <div 
                                onClick={() => canViewReport && toggleStudentExpanded(student.key)}
                                style={{ 
                                  fontWeight: '700', 
                                  fontSize: '15px', 
                                  color: '#0f172a',
                                  cursor: canViewReport ? 'pointer' : 'default',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  transition: 'color 0.15s ease'
                                }}
                                onMouseEnter={(e) => {
                                  if (canViewReport) e.currentTarget.style.color = '#2563eb';
                                }}
                                onMouseLeave={(e) => {
                                  if (canViewReport) e.currentTarget.style.color = '#0f172a';
                                }}
                                title={canViewReport ? "Click to view all reports for this student" : "Viewing reports is disabled by administrator"}
                              >
                                <span>{student.studentName}</span>
                                {canViewReport && (
                                  <div style={{ 
                                    transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)', 
                                    transition: 'transform 0.2s ease',
                                    color: '#2563eb',
                                    display: 'flex',
                                    alignItems: 'center'
                                  }}>
                                    <ChevronRight size={16} />
                                  </div>
                                )}
                              </div>
                              <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                                Enrolled Candidate • <span style={{ color: '#2563eb', fontWeight: '600' }}>{student.reports.length} {student.reports.length === 1 ? 'report' : 'reports'}</span>
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* USN / Roll No */}
                        <td style={{ padding: '16px 18px' }}>
                          {student.usn && student.usn !== 'N/A' ? (
                            <span style={{
                              display: 'inline-block',
                              background: '#f1f5f9',
                              color: '#334155',
                              padding: '4px 10px',
                              borderRadius: '6px',
                              fontSize: '12px',
                              fontWeight: '600',
                              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                              border: '1px solid #e2e8f0'
                            }}>
                              {student.usn}
                            </span>
                          ) : (
                            <span style={{ color: '#94a3b8', fontSize: '13px' }}>—</span>
                          )}
                        </td>

                        {/* Reports Count Pill */}
                        <td style={{ padding: '16px 18px' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <span style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: '3px 9px',
                              borderRadius: '14px',
                              fontSize: '12px',
                              fontWeight: '700',
                              background: '#eff6ff',
                              color: '#1d4ed8',
                              border: '1px solid #bfdbfe',
                              width: 'fit-content'
                            }}>
                              <FileText size={12} />
                              {student.reports.length} Total
                            </span>
                            <div style={{ fontSize: '11px', color: '#64748b' }}>
                              {student.interviewCount > 0 && `${student.interviewCount} Int `}
                              {student.interviewCount > 0 && student.counsellorCount > 0 && '• '}
                              {student.counsellorCount > 0 && `${student.counsellorCount} Coun`}
                            </div>
                          </div>
                        </td>

                        {/* Highest Marks Column */}
                        <td style={{ padding: '16px 18px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '5px',
                              padding: '4px 10px',
                              borderRadius: '8px',
                              fontSize: '13px',
                              fontWeight: '800',
                              background: scoreBadge.bg,
                              color: scoreBadge.text,
                              border: `1px solid ${scoreBadge.border}`
                            }}>
                              <Award size={13} />
                              {student.highestScore > 0 ? `${student.highestScore}/100` : '0/100'}
                            </span>
                          </div>
                          {student.reports.length > 1 && (
                            <div style={{ fontSize: '11px', color: '#64748b', marginTop: '3px' }}>
                              Avg: {student.averageScore}/100
                            </div>
                          )}
                        </td>

                        {/* Last Report Date & Type */}
                        <td style={{ padding: '16px 18px' }}>
                          {student.lastReport ? (
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#0f172a', fontSize: '13px', fontWeight: '600' }}>
                                <Calendar size={13} color="#94a3b8" />
                                <span>{lastReportDateInfo.date}</span>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '3px' }}>
                                <span style={{
                                  fontSize: '11px',
                                  fontWeight: '600',
                                  color: lastReportMode === 'interviewer' ? '#2563eb' : '#059669',
                                  background: lastReportMode === 'interviewer' ? '#eff6ff' : '#ecfdf5',
                                  padding: '1px 6px',
                                  borderRadius: '4px'
                                }}>
                                  {lastReportMode === 'interviewer' ? 'Interviewer' : 'Counsellor'}
                                </span>
                              </div>
                            </div>
                          ) : (
                            <span style={{ color: '#94a3b8', fontSize: '13px' }}>No session</span>
                          )}
                        </td>

                        {/* Action: Expand / View Reports */}
                        <td style={{ padding: '16px 24px', textAlign: 'right' }}>
                          {canViewReport ? (
                            <button
                              onClick={() => toggleStudentExpanded(student.key)}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '8px 14px',
                                borderRadius: '8px',
                                background: isExpanded ? '#2563eb' : '#ffffff',
                                color: isExpanded ? '#ffffff' : '#2563eb',
                                border: isExpanded ? '1px solid #2563eb' : '1px solid #bfdbfe',
                                fontSize: '13px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                boxShadow: '0 1px 2px rgba(37,99,235,0.06)',
                                transition: 'all 0.15s ease'
                              }}
                              onMouseEnter={(e) => {
                                if (!isExpanded) {
                                  e.currentTarget.style.background = '#eff6ff';
                                  e.currentTarget.style.borderColor = '#2563eb';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!isExpanded) {
                                  e.currentTarget.style.background = '#ffffff';
                                  e.currentTarget.style.borderColor = '#bfdbfe';
                                }
                              }}
                            >
                              <Eye size={14} />
                              <span>{isExpanded ? 'Hide' : `Reports (${student.reports.length})`}</span>
                              {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            </button>
                          ) : (
                            <button
                              disabled
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '8px 14px',
                                borderRadius: '8px',
                                background: '#f1f5f9',
                                color: '#94a3b8',
                                border: '1px solid #e2e8f0',
                                fontSize: '13px',
                                fontWeight: '600',
                                cursor: 'not-allowed'
                              }}
                              title="View Report action disabled by institution administrator"
                            >
                              <Lock size={14} />
                              <span>View Report</span>
                            </button>
                          )}
                        </td>

                      </tr>

                      {/* ── EXPANDED ACCORDION ROW: ALL STUDENT REPORTS ── */}
                      {isExpanded && canViewReport && (
                        <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                          <td colSpan={6} style={{ padding: '0 24px 20px 24px' }}>
                            <div style={{
                              background: '#ffffff',
                              borderRadius: '12px',
                              border: '1px solid #e2e8f0',
                              overflow: 'hidden',
                              boxShadow: '0 4px 6px -2px rgba(0,0,0,0.03)'
                            }}>
                              
                              {/* Accordion Card Header */}
                              <div style={{
                                padding: '14px 20px',
                                background: '#f1f5f9',
                                borderBottom: '1px solid #e2e8f0',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                              }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <FileText size={16} color="#2563eb" />
                                  <span style={{ fontWeight: '700', fontSize: '14px', color: '#0f172a' }}>
                                    All AI Evaluations for {student.studentName}
                                  </span>
                                  <span style={{
                                    background: '#2563eb',
                                    color: '#ffffff',
                                    borderRadius: '12px',
                                    padding: '2px 8px',
                                    fontSize: '11px',
                                    fontWeight: '700'
                                  }}>
                                    {student.reports.length} Total Sessions
                                  </span>
                                </div>
                                
                                <span style={{ fontSize: '12px', color: '#64748b' }}>
                                  Top Score: <strong style={{ color: scoreBadge.text }}>{student.highestScore}/100</strong> • Click any report to view comprehensive scorecard
                                </span>
                              </div>

                              {/* Nested Reports Table */}
                              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                                <thead>
                                  <tr style={{ background: '#ffffff', borderBottom: '1px solid #f1f5f9' }}>
                                    <th style={{ padding: '12px 20px', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', width: '6%' }}>
                                      #
                                    </th>
                                    <th style={{ padding: '12px 20px', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', width: '20%' }}>
                                      Assessment Agent
                                    </th>
                                    <th style={{ padding: '12px 20px', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', width: '25%' }}>
                                      Date Completed
                                    </th>
                                    <th style={{ padding: '12px 20px', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', width: '22%' }}>
                                      Role / Session Focus
                                    </th>
                                    <th style={{ padding: '12px 20px', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', width: '13%' }}>
                                      Marks / Score
                                    </th>
                                    <th style={{ padding: '12px 20px', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', textAlign: 'right', width: '14%' }}>
                                      Action
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {student.reports.map((report, idx) => {
                                    const repDateInfo = formatDate(report.createdAt);
                                    const isInt = report.mode === 'interviewer';
                                    const jobRole = report.report?.job_role || report.report?.role || (isInt ? 'Mock Interview' : 'Career Counselling');
                                    const repScoreBadge = getScoreBadgeProps(report.score || 0);

                                    return (
                                      <tr 
                                        key={report.id}
                                        style={{ 
                                          borderBottom: idx === student.reports.length - 1 ? 'none' : '1px solid #f1f5f9',
                                          transition: 'background-color 0.15s ease'
                                        }}
                                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f8fafc'; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#ffffff'; }}
                                      >
                                        <td style={{ padding: '12px 20px', fontSize: '12px', fontWeight: '600', color: '#94a3b8' }}>
                                          #{idx + 1}
                                        </td>
                                        <td style={{ padding: '12px 20px' }}>
                                          {isInt ? (
                                            <span style={{
                                              display: 'inline-flex',
                                              alignItems: 'center',
                                              gap: '5px',
                                              padding: '3px 10px',
                                              borderRadius: '14px',
                                              fontSize: '11px',
                                              fontWeight: '700',
                                              background: '#eff6ff',
                                              color: '#1d4ed8',
                                              border: '1px solid #bfdbfe'
                                            }}>
                                              <Briefcase size={12} /> AI Interviewer
                                            </span>
                                          ) : (
                                            <span style={{
                                              display: 'inline-flex',
                                              alignItems: 'center',
                                              gap: '5px',
                                              padding: '3px 10px',
                                              borderRadius: '14px',
                                              fontSize: '11px',
                                              fontWeight: '700',
                                              background: '#ecfdf5',
                                              color: '#047857',
                                              border: '1px solid #a7f3d0'
                                            }}>
                                              <Sparkles size={12} /> AI Counsellor
                                            </span>
                                          )}
                                        </td>
                                        <td style={{ padding: '12px 20px' }}>
                                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#334155' }}>
                                            <Calendar size={13} color="#94a3b8" />
                                            <span>{repDateInfo.date}</span>
                                            {repDateInfo.time && (
                                              <span style={{ color: '#64748b', fontSize: '12px' }}>• {repDateInfo.time}</span>
                                            )}
                                          </div>
                                        </td>
                                        <td style={{ padding: '12px 20px', fontSize: '13px', color: '#475569' }}>
                                          {jobRole}
                                        </td>
                                        <td style={{ padding: '12px 20px' }}>
                                          <span style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            padding: '2px 8px',
                                            borderRadius: '6px',
                                            fontSize: '12px',
                                            fontWeight: '700',
                                            background: repScoreBadge.bg,
                                            color: repScoreBadge.text,
                                            border: `1px solid ${repScoreBadge.border}`
                                          }}>
                                            {report.score > 0 ? `${report.score}/100` : '0/100'}
                                          </span>
                                        </td>
                                        <td style={{ padding: '12px 20px', textAlign: 'right' }}>
                                          {canViewReport ? (
                                            <button
                                              onClick={() => handleViewReport(report)}
                                              style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '5px',
                                                padding: '6px 12px',
                                                borderRadius: '6px',
                                                background: '#2563eb',
                                                color: '#ffffff',
                                                border: 'none',
                                                fontSize: '12px',
                                                fontWeight: '600',
                                                cursor: 'pointer',
                                                boxShadow: '0 1px 3px rgba(37,99,235,0.2)',
                                                transition: 'background 0.15s ease'
                                              }}
                                              onMouseEnter={(e) => { e.currentTarget.style.background = '#1d4ed8'; }}
                                              onMouseLeave={(e) => { e.currentTarget.style.background = '#2563eb'; }}
                                              title="View comprehensive AI scorecard"
                                            >
                                              <Eye size={13} />
                                              <span>View Report</span>
                                            </button>
                                          ) : (
                                            <button
                                              disabled
                                              style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '5px',
                                                padding: '6px 12px',
                                                borderRadius: '6px',
                                                background: '#f1f5f9',
                                                color: '#94a3b8',
                                                border: '1px solid #e2e8f0',
                                                fontSize: '12px',
                                                fontWeight: '600',
                                                cursor: 'not-allowed',
                                                boxShadow: 'none'
                                              }}
                                              title="Viewing detailed evaluation reports is disabled by administrator"
                                            >
                                              <Lock size={13} />
                                              <span>View Report</span>
                                            </button>
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>

                            </div>
                          </td>
                        </tr>
                      )}

                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* ── PAGINATION BAR (15 students per page) ── */}
          <div style={{
            padding: '16px 24px',
            background: '#ffffff',
            borderTop: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '12px'
          }}>
            {/* Status text */}
            <div style={{ fontSize: '13px', color: '#64748b' }}>
              Showing{' '}
              <strong style={{ color: '#0f172a' }}>
                {filteredStudents.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1}
              </strong>{' '}
              –{' '}
              <strong style={{ color: '#0f172a' }}>
                {Math.min(currentPage * PAGE_SIZE, filteredStudents.length)}
              </strong>{' '}
              of <strong style={{ color: '#0f172a' }}>{filteredStudents.length}</strong> student candidates
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {/* Previous Button */}
                <button
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '6px 12px',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    background: currentPage === 1 ? '#f8fafc' : '#ffffff',
                    color: currentPage === 1 ? '#94a3b8' : '#334155',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                  }}
                >
                  <ChevronLeft size={16} />
                  <span>Previous</span>
                </button>

                {/* Page Number Buttons */}
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => {
                  if (
                    totalPages > 7 &&
                    pageNum !== 1 &&
                    pageNum !== totalPages &&
                    Math.abs(pageNum - currentPage) > 1
                  ) {
                    if (pageNum === 2 || pageNum === totalPages - 1) {
                      return <span key={pageNum} style={{ color: '#94a3b8', padding: '0 4px' }}>…</span>;
                    }
                    return null;
                  }

                  const isActive = pageNum === currentPage;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      style={{
                        minWidth: '32px',
                        height: '32px',
                        borderRadius: '8px',
                        border: isActive ? '1px solid #2563eb' : '1px solid #e2e8f0',
                        background: isActive ? '#2563eb' : '#ffffff',
                        color: isActive ? '#ffffff' : '#334155',
                        fontSize: '13px',
                        fontWeight: '700',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease'
                      }}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                {/* Next Button */}
                <button
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '6px 12px',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    background: currentPage === totalPages ? '#f8fafc' : '#ffffff',
                    color: currentPage === totalPages ? '#94a3b8' : '#334155',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
                  }}
                >
                  <span>Next</span>
                  <ChevronRight size={16} />
                </button>
              </div>
            )}
          </div>

        </div>
      )}

      {/* ── RICH PERFORMANCE REPORT PORTAL ── */}
      {showModal && selectedReport && canViewReport && (
        <PerformanceReport 
          reportData={{
            ...selectedReport,
            report: selectedReport.report || selectedReport,
            studentName: selectedReport.studentName,
            usn: selectedReport.usn,
            createdAt: selectedReport.createdAt,
            completedAt: selectedReport.completedAt
          }}
          mode={selectedReport.mode}
          isHistoryView={true}
          backButtonText="Back to Reports Dashboard"
          onCloseReport={closeModal}
        />
      )}

    </div>
  );
};

export default UniAiReports;
